// Extracted from markdown.utils.ts so light consumers (format-compat, report utils) don't
// transitively import the remark/unified packages, which are ESM-only and unparseable by
// Jest's CJS transform. markdown.utils.ts re-exports this to preserve existing call sites.
export function isLikelyHtml(value: string | null | undefined): boolean {
  if (!value) return false;
  // Allow leading whitespace and any number of leading HTML comments, then
  // require a real tag head: <tagname[ attrs]> or <tagname/>.
  //
  // Accepts: "<p>...", "  <div>...", "<!-- generated --><p>..."
  // Rejects: "< 5 mg ibuprofen", "<-- ... -->", and "Summary:\n<p>..."
  //          (leading text is a backend contract bug, not HTML).
  // Rejects markdown documents that contain HTML islands further in the body —
  // those should still go through the markdown renderer.
  return /^\s*(?:<!--[\s\S]*?-->\s*)*<[a-z][a-z0-9]*\b[^<]*>/i.test(value);
}

/**
 * Drops orphan closing tags from the start of an HTML string, and the line break after them.
 *
 * AI output sometimes opens with a stray `</p>` (scribe 65607). `isLikelyHtml` then sees no
 * opening tag and the note goes to the markdown renderer, which prints every tag as text; and
 * even rendered as HTML, the orphan parses into an empty first paragraph. Strip it before
 * either decision. Indentation on the next line is kept, so indented markdown survives.
 * Linear time: every repeat has to begin with `</`, so there is only one way to match.
 */
export function stripLeadingClosingTags(value: string): string {
  return value.replace(/^(?:\s*<\/[a-z][\w:-]*\s*>)+[ \t]*(?:\r?\n)?/i, "");
}

const XHTML_NAMESPACE = "http://www.w3.org/1999/xhtml";

/** How the escaped run that Insert Scribe saved begins: a stray block close, then a block open. */
const ESCAPED_RUN_START =
  /<\/(?:p|div|li|h[1-6]|ol|ul)\s*>\s*<(?:p|div|h[1-6]|ol|ul|table|blockquote)\b/i;

/** Ancestors a run can end. The nearest one is the block the run is spliced back into. */
const SPLICE_BLOCKS = new Set([
  "div",
  "p",
  "li",
  "td",
  "th",
  "blockquote",
  "body"
]);

/** The only markup a decoded run may contain: plain formatting, never anything that loads or runs. */
const ALLOWED_DECODED_ELEMENTS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "sub",
  "sup",
  "hr",
  "pre",
  "code",
  "ol",
  "ul",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "span",
  "div",
  "blockquote",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td"
]);

/**
 * Elements that only make sense inside a particular parent. The template parser accepts them
 * anywhere, but spliced into the page a stray one closes or re-parents what is around it.
 */
const REQUIRED_PARENTS: Record<string, string[]> = {
  li: ["ol", "ul"],
  tr: ["table", "thead", "tbody", "tfoot"],
  td: ["tr"],
  th: ["tr"],
  thead: ["table"],
  tbody: ["table"],
  tfoot: ["table"]
};

/** A real note has at least one of these. A decoded run without one is not the artifact. */
const NOTE_BLOCK_ELEMENTS = new Set([
  "p",
  "ol",
  "ul",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6"
]);

/** Whitespace between two of these (or at an edge) carries no meaning and prints as a blank line. */
const BLOCK_LEVEL_ELEMENTS = new Set([
  ...NOTE_BLOCK_ELEMENTS,
  "li",
  "div",
  "blockquote",
  "pre",
  "hr",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td"
]);

/**
 * The rest of the summary is re-serialized after a repair. These elements parse differently in an
 * inert DOMParser document (scripting off) than on the page (scripting on), or hold raw text, so a
 * summary that contains one is left exactly as stored.
 */
const REFUSED_SURROUNDINGS =
  "noscript,template,svg,math,script,style,textarea,title,xmp,iframe,noembed,noframes,plaintext,object,embed,select";

const isWhitespaceOnly = (value: string) => /^\s*$/.test(value);

const isElement = (node: Node | null, name?: string): node is Element =>
  node?.nodeType === Node.ELEMENT_NODE &&
  (name === undefined || (node as Element).localName === name);

/** Empty-looking: whitespace text (`\s` includes nbsp) or a `<br>`. */
const isBlankNode = (node: Node) =>
  (node.nodeType === Node.TEXT_NODE &&
    isWhitespaceOnly(node.nodeValue ?? "")) ||
  isElement(node, "br");

const isBlockLevel = (node: Node | null) =>
  node === null ||
  (isElement(node) && BLOCK_LEVEL_ELEMENTS.has(node.localName));

/** Inert: a DOMParser document runs no script and loads no resource, unlike the live `document`. */
const parseBody = (html: string) =>
  new DOMParser().parseFromString(`<!DOCTYPE html><body>${html}`, "text/html");

const findSpliceBlock = (node: Node): Element | null => {
  let current = node.parentElement;
  while (current && !SPLICE_BLOCKS.has(current.localName)) {
    current = current.parentElement;
  }
  return current;
};

/** True when every sibling on the way from `node` up to `block`, in one direction, is blank. */
const isBlankToward = (
  node: Node,
  block: Element,
  direction: "previousSibling" | "nextSibling"
) => {
  for (let current: Node = node; current !== block; ) {
    for (
      let sibling = current[direction];
      sibling;
      sibling = sibling[direction]
    ) {
      if (!isBlankNode(sibling)) return false;
    }
    if (!current.parentNode) return false;
    current = current.parentNode;
  }
  return true;
};

/** The child of `block` that holds `node`. */
const topLevelChildOf = (node: Node, block: Element): Node => {
  let current = node;
  while (current.parentNode && current.parentNode !== block) {
    current = current.parentNode;
  }
  return current;
};

const hasRequiredParent = (element: Element) => {
  const parents = REQUIRED_PARENTS[element.localName];
  return !parents || parents.includes(element.parentElement?.localName ?? "");
};

/**
 * Whitespace between two blocks, or between a block and the edge of its container, prints as a
 * blank line. Whitespace that is an element's only content (`<p>&nbsp;</p>`, `<span> </span>`) is
 * the content, and stays.
 */
const isBlankBetweenBlocks = (node: Node) =>
  isWhitespaceOnly(node.nodeValue ?? "") &&
  (node.previousSibling !== null || node.nextSibling !== null) &&
  isBlockLevel(node.previousSibling) &&
  isBlockLevel(node.nextSibling);

/**
 * Parses a decoded run in the inert document and returns it only if it is plain formatting: text
 * plus allowlisted HTML elements with no attributes, each where its parent allows it, and at least
 * one note block. Anything else (a comment, `<img>`, an `on*` attribute, foreign content, a stray
 * `<li>`) refuses the whole run.
 */
const parseDecodedRun = (doc: Document, decoded: string) => {
  const template = doc.createElement("template");
  template.innerHTML = decoded;
  const fragment = template.content;
  const walker = doc.createTreeWalker(fragment, NodeFilter.SHOW_ALL);
  const blankBetweenBlocks: Node[] = [];
  let hasNoteBlock = false;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (isBlankBetweenBlocks(node)) blankBetweenBlocks.push(node);
      continue;
    }
    if (
      !isElement(node) ||
      node.namespaceURI !== XHTML_NAMESPACE ||
      !ALLOWED_DECODED_ELEMENTS.has(node.localName) ||
      node.attributes.length > 0 ||
      !hasRequiredParent(node)
    ) {
      return null;
    }
    if (NOTE_BLOCK_ELEMENTS.has(node.localName)) hasNoteBlock = true;
  }
  if (!hasNoteBlock) return null;
  // The parser silently drops tags that cannot go where they are (a `<tr>` after a `<p>`), and the
  // result still parses back to itself. Fewer elements than start tags means markup was lost.
  const startTags = decoded.match(/<[a-z]/gi)?.length ?? 0;
  if (fragment.querySelectorAll("*").length < startTags) return null;
  blankBetweenBlocks.forEach((node) => node.parentNode?.removeChild(node));
  return fragment;
};

/**
 * Repairs report summaries saved while Insert Scribe escaped a note that opened with a stray `</p>`
 * (DEV-3624). `isLikelyHtml` sent that note to markdown, which escaped every tag, so the summary
 * holds the AI's HTML as TEXT: `<div>&lt;/p&gt; &lt;p&gt;&lt;strong&gt;…</div>`. This decodes the
 * run back into markup.
 *
 * Returns the input unchanged (the same value) unless it finds that run, at the end of its block,
 * after nothing but plain text, and the decoded run is only plain formatting. The summary is
 * rendered with `dangerouslySetInnerHTML`, so anything else is refused, never sanitised. A repair is
 * kept only if its output parses back to itself.
 *
 * Cheap for every other summary: one substring scan, and no parse unless `&lt;/` is present.
 */
export function repairEscapedScribeHtml<T extends string | null | undefined>(
  html: T
): T {
  if (!html || !html.includes("&lt;/") || typeof DOMParser === "undefined") {
    return html;
  }
  const doc = parseBody(html);
  const { body } = doc;
  if (body.querySelector(REFUSED_SURROUNDINGS)) return html;

  const candidates: Text[] = [];
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (ESCAPED_RUN_START.test(node.nodeValue ?? "")) {
      candidates.push(node as Text);
    }
  }

  let repaired = false;
  candidates.forEach((text) => {
    const block = findSpliceBlock(text);
    if (!block || !body.contains(text)) return;
    const start = text.data.search(ESCAPED_RUN_START);
    const prefix = text.data.slice(0, start);
    if (/[<>]/.test(prefix) || !isBlankToward(text, block, "nextSibling")) {
      return;
    }
    const fragment = parseDecodedRun(
      doc,
      stripLeadingClosingTags(text.data.slice(start)).trim()
    );
    if (!fragment) return;

    const hasPrefix =
      !isWhitespaceOnly(prefix) ||
      !isBlankToward(text, block, "previousSibling");
    if (!hasPrefix) {
      if (block.localName === "p") {
        block.parentNode?.replaceChild(fragment, block);
      } else {
        block.textContent = "";
        block.appendChild(fragment);
      }
    } else {
      text.data = prefix;
      const anchor =
        block.localName === "p" ? block : topLevelChildOf(text, block);
      anchor.parentNode?.insertBefore(fragment, anchor.nextSibling);
    }
    repaired = true;
  });

  if (!repaired) return html;
  // Defence in depth: no known input reaches this (the start-tag and parent guards in
  // `parseDecodedRun` catch the ones that did), but a repair the page would re-arrange must never
  // be returned. It is all or nothing, so it must stay a backstop, not the per-run check.
  const output = body.innerHTML;
  return (parseBody(output).body.innerHTML === output ? output : html) as T;
}
